import React, { useMemo, ReactNode } from "react";
import { LocalizationScope } from "../../util";
import { FormContext } from "./context";
import { FormFields } from "./Fields";
import FormGroup from "./Group";
import FormFieldSet from "./FieldSet";
import FormActions from "./Actions";
import { HintAndTerms as FormHintAndTerms } from "./HintAndTerms";

export {
  checkErrors,
  initializeFields,
  flatFields,
  patchFields,
  modifyFields,
  updateFieldValue,
  collectParams,
  formatValue,
  OBJECT_SEPARATOR,
} from "./helper";
export { useForm, useMultiForm, fieldTypes, limitFields, setupFields } from "./useForm";
export type { MultiFormState, UseMultiFormReturn, UseMultiFormOpts, UseMultiFormEntry } from "./useForm";
export type { FormContextValue } from "./context";

function preventDefault(e: any) {
  e.preventDefault();
}

const SCOPE = ["", "::PSS::Form"];

export interface FormProps {
  /**
   * Layout variant for the form.
   * - "wide": Centers content with max width of 800px
   * - "narrow": Full-width flex column layout
   * @default "wide"
   */
  variant?: "wide" | "narrow";
  /** Custom field type renderers that extend the built-in type map */
  customRenderers?: Record<string, React.ComponentType<any>>;
  /** Additional CSS class name(s) applied to the rendered <form> element. */
  className?: string;
  /** Form content, typically Form.Fields */
  children?: ReactNode;
}

/**
 * Form container component that provides context for form fields and handles form submission.
 * Works with the useForm hook to manage field state and validation.
 *
 * @example
 * ```tsx
 * const initialFields = [
 *   { id: "name", type: fieldTypes.TEXT, label: "Name", value: "" },
 *   { id: "email", type: fieldTypes.TEXT, label: "Email", value: "" }
 * ];
 *
 * const { fields, changeField } = useForm(initialFields);
 *
 * <Form>
 *   <Form.Fields fields={fields} onChangeField={changeField} />
 * </Form>
 * ```
 */
export function Form({
  variant = "wide",
  customRenderers,
  className,
  children,
}: FormProps) {
  const context = useMemo(
    () => ({ variant, customRenderers }),
    [variant, customRenderers],
  );
  return (
    <LocalizationScope scope={SCOPE}>
      <FormContext.Provider value={context}>
        <form className={className} onSubmit={preventDefault}>
          {children}
        </form>
      </FormContext.Provider>
    </LocalizationScope>
  );
}

/** Provides FormContext (customRenderers, variant) without rendering a <form> element. */
function FormProvider({
  variant = "wide",
  customRenderers,
  children,
}: FormProps) {
  const context = useMemo(
    () => ({ variant, customRenderers }),
    [variant, customRenderers],
  );
  return (
    <FormContext.Provider value={context}>{children}</FormContext.Provider>
  );
}

Form.Fields = FormFields;
Form.Group = FormGroup;
Form.FieldSet = FormFieldSet;
Form.Actions = FormActions;
Form.HintAndTerms = FormHintAndTerms;
Form.Provider = FormProvider;
